import React from 'react';
import { Badge, DataTable, RowActions, type DataTableColumn } from '@/components/ui';
import { ServiceArea } from '../../../services/areasService';
import { useClientSort } from '../../../utils/useClientSort';

interface ServiceAreasTableProps {
  serviceAreas: ServiceArea[];
  onEdit: (serviceArea: ServiceArea) => void;
  onDelete: (id: number) => void;
  canEdit: boolean;
  canDelete: boolean;
}

const SORT_VALUES: Record<string, (sa: ServiceArea) => string | number | null | undefined> = {
  id: (sa) => sa.id,
  name: (sa) => sa.name,
  code: (sa) => sa.code,
  foremanAreas: (sa) => sa.foremanAreas?.length || 0,
};

const ServiceAreasTable: React.FC<ServiceAreasTableProps> = ({
  serviceAreas,
  onEdit,
  onDelete,
  canEdit,
  canDelete
}) => {
  const { sorted, sortBy, sortDir, onSort } = useClientSort(serviceAreas, SORT_VALUES);

  const columns: DataTableColumn<ServiceArea>[] = [
    { key: 'id', header: 'ID', sortable: true, cell: (sa) => sa.id, mobile: 'hidden' },
    { key: 'name', header: 'Name', sortable: true, cell: (sa) => <strong>{sa.name}</strong>, mobile: 'title' },
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      mobileLabel: 'Code',
      cell: (sa) =>
        sa.code ? <Badge bg="secondary">{sa.code}</Badge> : <span className="tw-text-content-muted">-</span>,
    },
    {
      key: 'foremanAreas',
      header: 'Foreman Areas',
      sortable: true,
      mobileLabel: 'Foreman Areas',
      cell: (sa) => <Badge bg="info">{sa.foremanAreas?.length || 0} areas</Badge>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      hidden: !(canEdit || canDelete),
      cell: (sa) => {
        const hasChildren = !!sa.foremanAreas && sa.foremanAreas.length > 0;
        return (
          <RowActions
            actions={[
              {
                kind: 'edit',
                label: 'Edit Service Area',
                onClick: () => onEdit(sa),
                hidden: !canEdit,
              },
              {
                kind: 'delete',
                // The disabled reason rides in the label — a kebab item has no hover target.
                label: hasChildren ? 'Delete Service Area — has foreman areas' : 'Delete Service Area',
                onClick: () => onDelete(sa.id),
                disabled: hasChildren,
                hidden: !canDelete,
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={sorted}
      rowKey={(sa) => sa.id}
      striped
      bordered
      hover
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={onSort}
      emptyMessage="No service areas found."
    />
  );
};

export default ServiceAreasTable;
